import { formatDate, formatGender, formatPhoneNumber, getAge } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { EnrollmentCheckDate, EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import {
    AddressDetailLabelValue,
    DetailLabel,
    DetailLabelValuePair,
} from '@strata/react-label-value-pairs';
import { IssueDetail } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { LabelWithIssue } from 'components/opportunities/LabelWithIssue';
import { AdditionalBenefitInfoDialog } from 'components/referrals/dialogs/AdditionalBenefitInfoDialog';
import {
    getMemberEligibilityStatusDisplay,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';

export const OpportunityMemberDetails = ({
    opportunityDetails = undefined,
    memberDetails = undefined,
    willBeUnmanagedOn = undefined,
    // eslint-disable-next-line complexity
}) => {
    const { agoraEnhancedEligibility } = useFlags();
    const [isAdditionalBenefitInfoDialogOpen, setIsAdditionalBenefitInfoDialogOpen] =
        useState(false);

    const eligibilityDisplay = memberDetails
        ? getMemberEligibilityStatusDisplay(memberDetails.enrollmentStatus)
        : null;
    const showWillBeUnmanagedOn =
        memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING && !!willBeUnmanagedOn;

    return (
        <FormSectionWithRows
            sectionTitle="Member Details"
            width="100%"
            isLoading={!memberDetails}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Gender"
                        value={formatGender(memberDetails?.gender)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="DOB"
                        value={formatDate(memberDetails?.dateOfBirth)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Age"
                        value={getAge(memberDetails?.dateOfBirth)}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <AddressDetailLabelValue
                        label={
                            <LabelWithIssue
                                text="Address"
                                issues={opportunityDetails?.issues}
                                issueType={IssueDetail.IssueTypes.INVALID_MEMBER_ADDRESS}
                            />
                        }
                        style={{ width: '33%' }}
                        address1={memberDetails?.memberAddress1}
                        address2={memberDetails?.memberAddress2}
                        city={memberDetails?.memberCity}
                        state={memberDetails?.memberState}
                        zip={memberDetails?.memberZip}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Phone"
                        value={formatPhoneNumber(memberDetails?.phone)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Email"
                        value={<MailToHyperlink emailAddress={memberDetails?.email} />}
                    />
                </>
            }
            rowThreeContent={
                <>
                    <AdditionalBenefitInfoDialog
                        payerId={opportunityDetails?.payerId}
                        benefitGroup={memberDetails?.benefitGroup}
                        memberDetails={memberDetails}
                        isOpen={isAdditionalBenefitInfoDialogOpen}
                        onClose={() => setIsAdditionalBenefitInfoDialogOpen(false)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '33%' }}
                        label="Group Id"
                        value={
                            memberDetails?.benefitGroup ? (
                                <ActionButtonText
                                    label={memberDetails.benefitGroup}
                                    onClick={() => setIsAdditionalBenefitInfoDialogOpen(true)}
                                />
                            ) : undefined
                        }
                    />
                    <DetailLabelValuePair
                        style={{ width: '34%' }}
                        label="Member Id"
                        value={memberDetails?.memberId}
                    />
                </>
            }
            rowFourContent={
                agoraEnhancedEligibility && eligibilityDisplay ? (
                    <>
                        <div style={{ width: '33%' }}>
                            <DetailLabel label="Enrollment Status" />
                            <StatusIndicator
                                message={eligibilityDisplay.label}
                                status={eligibilityDisplay.status}
                                tooltipText={eligibilityDisplay.tooltipText}
                            />
                        </div>
                        {showWillBeUnmanagedOn ? (
                            <DetailLabelValuePair
                                style={{ width: '33%' }}
                                label="Grace period ends"
                                value={formatDate(willBeUnmanagedOn)}
                            />
                        ) : null}
                    </>
                ) : undefined
            }
            rowFiveContent={
                agoraEnhancedEligibility && eligibilityDisplay ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '33%' }}
                            label="Coverage Dates"
                            value={
                                <EnrollmentCoverageDates
                                    enrollmentStart={memberDetails?.enrollmentStart}
                                    enrollmentEnd={memberDetails?.enrollmentEnd}
                                    isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                                />
                            }
                        />
                        <DetailLabelValuePair
                            style={{ width: '33%' }}
                            label="Enrollment Check Date"
                            value={
                                <EnrollmentCheckDate
                                    enrollmentCheckDate={memberDetails?.enrollmentCheckDate}
                                />
                            }
                        />
                    </>
                ) : undefined
            }
        />
    );
};

OpportunityMemberDetails.propTypes = {
    opportunityDetails: PropTypes.shape({
        referralId: PropTypes.string,
        payerId: PropTypes.string,
        issues: PropTypes.arrayOf(PropTypes.string),
    }),
    willBeUnmanagedOn: PropTypes.string,
    memberDetails: PropTypes.shape({
        gender: PropTypes.string,
        dateOfBirth: PropTypes.string,
        memberAddress1: PropTypes.string,
        memberAddress2: PropTypes.string,
        memberCity: PropTypes.string,
        memberState: PropTypes.string,
        memberZip: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        benefitGroup: PropTypes.string,
        memberId: PropTypes.string,
        enrollmentStatus: PropTypes.string,
        lastUpdated: PropTypes.string,
    }),
};
